import React from "react";
import { QuestionItem } from "../../../types/form";
import type { CompositeFieldDef } from "../../../utils/compositeValidation";
import { CompositeSubFieldControl } from "./CompositeSubFieldControl";

type CompositeLayoutContext = {
  useGrid: boolean;
  isAddressGroup: boolean;
  isHeightGroup: boolean;
};

function fieldSearchText(field: CompositeFieldDef): string {
  return `${field.key ?? ""} ${field.label ?? ""}`.toLowerCase();
}

function hasFieldMatch(
  fields: CompositeFieldDef[],
  matcher: (text: string) => boolean,
): boolean {
  return fields.some((field) => matcher(fieldSearchText(field)));
}

function isAddressRelatedText(text: string): boolean {
  return (
    /address/.test(text) ||
    /city|town/.test(text) ||
    /\bstate\b/.test(text) ||
    /\bzip\b/.test(text) ||
    /province/.test(text) ||
    /postal/.test(text) ||
    /country/.test(text)
  );
}

function getCompositeLayoutContext(
  fields: CompositeFieldDef[],
): CompositeLayoutContext {
  const isHeightGroup =
    hasFieldMatch(fields, (text) => /\bfeet\b|\bfoot\b/.test(text)) &&
    hasFieldMatch(fields, (text) => /\binches\b|\binch\b/.test(text));

  const addressSignals = fields.filter((field) =>
    isAddressRelatedText(fieldSearchText(field)),
  ).length;
  const isAddressGroup = addressSignals >= 2;

  return {
    useGrid: isHeightGroup || isAddressGroup,
    isAddressGroup,
    isHeightGroup,
  };
}

function getFieldGridClass(
  field: CompositeFieldDef,
  layout: CompositeLayoutContext,
): string {
  const text = fieldSearchText(field);

  if (layout.isHeightGroup) {
    if (/\bfeet\b|\bfoot\b|\binches\b|\binch\b/.test(text)) {
      return "md:col-span-6";
    }
  }

  if (layout.isAddressGroup) {
    if (/address[_\s-]*type/.test(text)) return "md:col-span-12";
    if (
      /street.*name/.test(text) ||
      /street|address line|address1|address 1|address2|address 2/.test(text)
    ) {
      return "sm:col-span-12";
    }
    if (/street.*number|house.*number|address.*number|\bnumber\b/.test(text)) {
      return "sm:col-span-4";
    }
    if (
      /city|town|\bstate\b|\bzip\b|province|postal|country/.test(text)
    ) {
      return "sm:col-span-6 lg:col-span-4";
    }
  }

  return "md:col-span-12";
}

function getFieldControlClass(field: CompositeFieldDef): string | undefined {
  const text = fieldSearchText(field);
  const fieldType = field.type?.toLowerCase() || "text";

  if (fieldType === "radio" && /address[_\s-]*type/.test(text)) {
    return "grid grid-cols-1 gap-2 space-y-0 sm:grid-cols-3 [&>label]:mb-0";
  }

  if (fieldType === "radio") {
    return "[&>label]:mb-0";
  }

  return undefined;
}

interface CompositeFieldRendererProps {
  question: QuestionItem;
  inputName: string;
  currentValue: any;
  compositeAnswer: Record<string, any>;
  handleCompositeFieldChange: (
    inputName: string,
    compositeAnswer: Record<string, any>,
    fieldKey: string,
    value: any,
  ) => void;
  handleMultipleCompositeChange: (
    inputName: string,
    entryIdx: number,
    fieldKey: string,
    value: any,
  ) => void;
  handleAddEntry: (inputName: string) => void;
  handleRemoveEntry: (inputName: string, entryIdx: number) => void;
  readOnly?: boolean;
}

function renderFieldBlock(
  field: CompositeFieldDef,
  fieldId: string,
  fieldValue: unknown,
  readOnly: boolean,
  onChange: (value: unknown) => void,
  className?: string,
) {
  return (
    <div key={field.key} className={`space-y-2 ${className ?? ""}`}>
      <label
        htmlFor={fieldId}
        className="mb-0 block text-sm font-medium text-[#1B365D]"
      >
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <CompositeSubFieldControl
        field={field}
        fieldId={fieldId}
        fieldValue={fieldValue}
        readOnly={readOnly}
        controlClassName={getFieldControlClass(field)}
        onChange={onChange}
      />
    </div>
  );
}

const N400_EMPLOYMENT_QUESTION_ID = 65;
const EMPLOYMENT_END_FIELD_KEY = "EMP_TO";
const PRESENT_VALUE = "PRESENT";

function isN400EmploymentQuestion(question: QuestionItem): boolean {
  return Number(question.id ?? question.originalQuestion?.id) === N400_EMPLOYMENT_QUESTION_ID;
}

function renderFirstEmploymentEndDateBlock(
  field: CompositeFieldDef,
  fieldId: string,
  fieldValue: unknown,
  readOnly: boolean,
  onChange: (value: unknown) => void,
  className?: string,
) {
  const isPresent = String(fieldValue ?? "").trim().toUpperCase() === PRESENT_VALUE;
  const presentId = `${fieldId}_present`;

  return (
    <div key={field.key} className={`space-y-2 ${className ?? ""}`}>
      <label
        htmlFor={fieldId}
        className="mb-0 block text-sm font-medium text-[#1B365D]"
      >
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <label
        htmlFor={presentId}
        className="flex items-center gap-2 text-sm text-[#1B365D]"
      >
        <input
          id={presentId}
          type="checkbox"
          checked={isPresent}
          disabled={readOnly}
          onChange={(event) => onChange(event.target.checked ? PRESENT_VALUE : "")}
          className="h-4 w-4 rounded border-gray-300 text-[#1B365D] focus:ring-[#1B365D]"
        />
        I currently work or study here
      </label>
      <CompositeSubFieldControl
        field={isPresent ? { ...field, type: "text" } : field}
        fieldId={fieldId}
        fieldValue={isPresent ? PRESENT_VALUE : fieldValue}
        readOnly={readOnly || isPresent}
        controlClassName={getFieldControlClass(field)}
        onChange={onChange}
      />
    </div>
  );
}

const CompositeFieldRenderer: React.FC<CompositeFieldRendererProps> = ({
  question,
  inputName,
  currentValue,
  compositeAnswer,
  handleCompositeFieldChange,
  handleMultipleCompositeChange,
  handleAddEntry,
  handleRemoveEntry,
  readOnly = false,
}) => {
  const fields =
    question.validationRules?.fields ||
    question.originalQuestion?.validationRules?.fields ||
    [];

  if (!Array.isArray(fields) || fields.length === 0) {
    return <p className="text-red-500">Invalid composite question</p>;
  }

  const typedFields = fields as CompositeFieldDef[];
  const layout = getCompositeLayoutContext(typedFields);
  const fieldsContainerClass = layout.useGrid
    ? "grid grid-cols-1 gap-4 sm:grid-cols-12"
    : "space-y-4";

  if (question.allowMultipleAnswers) {
    const compositeEntries = Array.isArray(currentValue)
      ? currentValue
      : [currentValue || {}];

    return (
      <div className="space-y-6">
        {compositeEntries.map((entry, entryIdx) => (
          <div
            key={entryIdx}
            className="border border-gray-200 rounded-lg p-4 space-y-4 bg-gray-50"
          >
            {entryIdx > 0 && (
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-[#1B365D]">
                  Entry {entryIdx + 1}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveEntry(inputName, entryIdx)}
                  disabled={readOnly}
                  className="px-3 py-1 text-sm text-red-600 border border-red-600 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Remove
                </button>
              </div>
            )}
            <div className={fieldsContainerClass}>
              {typedFields.map((field) => {
                const fieldId = `${inputName}_${entryIdx}_${field.key}`;
                const fieldValue = entry?.[field.key] || "";
                const onChange = (value: unknown) =>
                  handleMultipleCompositeChange(
                    inputName,
                    entryIdx,
                    field.key,
                    value,
                  );
                const className = layout.useGrid
                  ? getFieldGridClass(field, layout)
                  : undefined;

                if (
                  isN400EmploymentQuestion(question) &&
                  entryIdx === 0 &&
                  field.key === EMPLOYMENT_END_FIELD_KEY
                ) {
                  return renderFirstEmploymentEndDateBlock(
                    field,
                    fieldId,
                    fieldValue,
                    readOnly,
                    onChange,
                    className,
                  );
                }

                return renderFieldBlock(
                  field,
                  fieldId,
                  fieldValue,
                  readOnly,
                  onChange,
                  className,
                );
              })}
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() => handleAddEntry(inputName)}
          disabled={readOnly}
          className="px-4 py-2 bg-[#1B365D] text-white rounded-md hover:bg-[#152a4a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          + Add Another
        </button>
      </div>
    );
  }

  return (
    <div className={fieldsContainerClass}>
      {typedFields.map((field) =>
        renderFieldBlock(
          field,
          `${inputName}_${field.key}`,
          compositeAnswer?.[field.key] || "",
          readOnly,
          (value) =>
            handleCompositeFieldChange(
              inputName,
              compositeAnswer,
              field.key,
              value,
            ),
          layout.useGrid ? getFieldGridClass(field, layout) : undefined,
        ),
      )}
    </div>
  );
};

export default CompositeFieldRenderer;
